import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { SUBSCRIPTION_LOOKUP_FAILED_MESSAGE } from '../lib/userMessages';
import { retryAsync } from '../lib/retry';

const SubscriptionContext = createContext();
const SUBSCRIPTION_CACHE_PREFIX = 'ss_subscription_cache_v1:';
const PROFILE_LOOKUP_TIMEOUT_MS = 8000;

function readCachedSubscription(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(`${SUBSCRIPTION_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedSubscription(userId, data) {
  if (!userId || !data) return;
  try {
    localStorage.setItem(`${SUBSCRIPTION_CACHE_PREFIX}${userId}`, JSON.stringify({
      subscription_tier: data.subscription_tier || 'standard',
      pro_councils: data.pro_councils || [],
      pro_council_count: data.pro_council_count || 0,
      subscription_status: data.subscription_status || null,
      voucher_trial_ends_at: data.voucher_trial_ends_at || null,
      cached_at: new Date().toISOString(),
    }));
  } catch {
    /* ignore cache failures */
  }
}

function isActiveCachedSubscription(cached) {
  return cached?.subscription_status === 'active';
}

function withTimeout(promise, ms, label) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(`${label} timed out`);
      error.name = 'SubscriptionLookupTimeoutError';
      reject(error);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [tier, setTier] = useState('standard');
  const [proCouncils, setProCouncils] = useState([]);
  const [proCouncilCount, setProCouncilCount] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [voucherTrialEndsAt, setVoucherTrialEndsAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    if (!user) {
      setTier('standard');
      setProCouncils([]);
      setProCouncilCount(0);
      setSubscriptionStatus(null);
      setVoucherTrialEndsAt(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const cached = readCachedSubscription(user.id);
    if (cached) {
      setTier(cached.subscription_tier || 'standard');
      setProCouncils(cached.pro_councils || []);
      setProCouncilCount(cached.pro_council_count || 0);
      setSubscriptionStatus(cached.subscription_status || null);
      setVoucherTrialEndsAt(cached.voucher_trial_ends_at || null);
      setLoading(false);
    }
    setLoading(!cached);
    setError(null);
    retryAsync(async () => {
      if (typeof supabase.rpc === 'function') {
        try {
          await supabase.rpc('expire_own_voucher_trial_if_needed');
        } catch {
          // Best-effort housekeeping only; never block profile lookup.
        }
      }
      const { data, error } = await withTimeout(
        supabase
          .from('profiles')
          .select('subscription_tier, pro_councils, pro_council_count, subscription_status, voucher_trial_ends_at')
          .eq('id', user.id)
          .maybeSingle(),
        PROFILE_LOOKUP_TIMEOUT_MS,
        'Subscription profile lookup'
      );
      if (error) throw error;
      return data;
    }, {
      retries: 2,
      baseDelayMs: 250,
      onRetry: (error, attempt) => {
        console.warn(`[SiteScope] SubscriptionContext: retrying profile lookup (${attempt})`, error.message || error);
      },
    })
      .then(data => {
        if (cancelled) return;
        if (!data) {
          console.warn('[SiteScope] SubscriptionContext: no profile for user', user.id);
          setError(isActiveCachedSubscription(cached) ? null : SUBSCRIPTION_LOOKUP_FAILED_MESSAGE);
          setLoading(false);
          return;
        }
        const resolvedTier   = data?.subscription_tier   || 'standard';
        const resolvedCouncils = data?.pro_councils       || [];
        const resolvedCount  = data?.pro_council_count    || 0;
        const resolvedStatus = data?.subscription_status  || null;
        const resolvedTrialEndsAt = data?.voucher_trial_ends_at || null;
        setTier(resolvedTier);
        setProCouncils(resolvedCouncils);
        setProCouncilCount(resolvedCount);
        setSubscriptionStatus(resolvedStatus);
        setVoucherTrialEndsAt(resolvedTrialEndsAt);
        setError(null);
        writeCachedSubscription(user.id, data);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[SiteScope] SubscriptionContext: profile request failed', err.message || err);
        setError(isActiveCachedSubscription(cached) ? null : SUBSCRIPTION_LOOKUP_FAILED_MESSAGE);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user?.id, refreshKey]);

  const isPro = ['pro', 'trade', 'business'].includes(tier);

  const subscription = {
    tier,
    isPro,
    isStandard: tier === 'standard',
    proCouncils,
    proCouncilCount,
    subscriptionStatus,
    voucherTrialEndsAt,
    loading,
    error,
    refresh,
  };

  return (
    <SubscriptionContext.Provider value={subscription}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) throw new Error('useSubscription must be used within SubscriptionProvider');
  return context;
}
